import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from './useReduxAuth';
import { useNotification } from './useNotification';
import { useTranslation } from 'react-i18next';
import { USER_ROLES } from '../constants/permissions';
import { hasuraClient } from '../utils/graphqlClient';
import {
  GET_DOCUMENT_BANK_QUERY,
  GET_LATEST_VERSION_NUMBER_QUERY,
  GET_DOCUMENT_BANK_TAGS_QUERY,
} from '../graphql/queries';
import {
  CREATE_DOCUMENT_BANK_MUTATION,
  UPDATE_DOCUMENT_BANK_MUTATION,
  TOGGLE_DOCUMENT_BANK_ACTIVE_MUTATION,
  DELETE_DOCUMENT_BANK_MUTATION,
  CREATE_DOCUMENT_BANK_VERSION_MUTATION,
  REPLACE_DOCUMENT_BANK_ASSIGNMENTS_MUTATION,
  REPLACE_DOCUMENT_BANK_TAG_ASSIGNMENTS_MUTATION,
} from '../graphql/mutations';
import s3Service from '../services/s3Service';
import { getDocumentFileTypeMeta } from '../constants/tableConfigurations';

const S3_FOLDER = 'document-bank';

// Row'lari client-side filter/search icin flatten et:
//   uploaderName / fileTypeLabel — DataTable bu top-level alanlar uzerinden arar
//   status — is_active boolean'ini leaveTypes/partners pattern'i gibi
//           'active' | 'inactive' string'e cevirir (multiselect `exact` filter
//           bu string uzerinden calisir; boolean field'i standart filter loop'u
//           dogru karsilastiramaz).
//   tags  — jobs.tags pattern'i: array of {id, name, color}. DataTableContainer
//           `field === 'tags'` icin ozel branch'e sahip ve seciliyi item.tags[]
//           icinde arar.
// `tagsById` — bank.tags'ten olusturulan id->{id,name,color} lookup.
// Enrichment sirasinda tag_assignments.tag relation'i null gelirse (Hasura
// relation permission gecmedigi ana durumda) bank.tags'ten name/color fallback
// alinir; boylece filter/kolon "1507" gibi ham ID goruntulemez.
const enrichDocument = (doc, tagsById = new Map()) => {
  const latest = doc?.versions?.[0] || null;
  const uploader = latest?.uploader;
  const uploaderName = uploader
    ? [uploader.first_name, uploader.last_name].filter(Boolean).join(' ').trim()
    : '';
  const meta = getDocumentFileTypeMeta(latest?.mime_type || latest?.file_name);
  const tags = (doc?.tag_assignments || [])
    .map(a => {
      // Onceligi relation'dan gelene ver — yoksa lookup'tan hidrate et.
      if (a?.tag && a.tag.name) return a.tag;
      const fromLookup = a?.tag_id != null ? tagsById.get(a.tag_id) : null;
      if (fromLookup) return fromLookup;
      // Son care: tag_id ile placeholder — name yerine bosluk gostermek daha
      // dogru olurdu ama en azindan filter'a girmesin diye null dondur.
      return null;
    })
    .filter(Boolean);
  return {
    ...doc,
    uploaderName,
    fileTypeLabel: meta.label,
    status: doc?.is_active === false ? 'inactive' : 'active',
    tags,
    // Filter icin flat ID array — field ismi 'tags' degil 'docTagIds'
    // ile ayirdik ki DataTable'in job-tag'e ozel effect'i tetiklenmesin.
    // Standart multiselect filter: value=[id] some includes docTagIds.
    docTagIds: tags.map(t => t.id).filter(v => v != null),
  };
};

const useDocumentBank = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [documents, setDocuments] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isRootUser = useMemo(() => user?.role === USER_ROLES.ROOT, [user?.role]);

  const fetchDocuments = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Dokumanlar + etiketler tek round-trip'te — filter dropdown'lari
      // etiket listesini hazir bekliyor.
      const [docsData, tagsData] = await Promise.all([
        hasuraClient.query(GET_DOCUMENT_BANK_QUERY),
        hasuraClient.query(GET_DOCUMENT_BANK_TAGS_QUERY),
      ]);
      const tagList = tagsData?.document_bank_tags || [];
      setTags(tagList);
      // Fallback lookup — Hasura tag relation permission gecmezse enrichment
      // buradan name/color hidrasyonu yapar. Iki fetch paralel oldugu icin
      // tagsData'yi bekliyoruz.
      const tagsById = new Map(tagList.map(tg => [tg.id, tg]));
      const raw = docsData?.document_bank || [];
      setDocuments(raw.map(d => enrichDocument(d, tagsById)));
    } catch (err) {
      showNotification(
        t('documentBank.notifications.loadError', 'Doküman bankası yüklenemedi'),
        'error'
      );
      setDocuments([]);
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, [user, showNotification, t]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Ortak: dosyayi S3'e yukleyip URL/metadata dondurur.
  // NOT: Bucket private oldugu icin ham public S3 URL'i AccessDenied doner.
  // `file_url` kolonunda URL yerine S3 KEY saklariz (kolon adi legacy) —
  // indirme aninda `s3Service.getSignedUrl(key)` ile fresh presigned URL alinir.
  const uploadFile = useCallback(async (file) => {
    const uploadResult = await s3Service.uploadFile(file, S3_FOLDER);
    if (!uploadResult || uploadResult.success === false) {
      const msg = uploadResult?.error || t('documentBank.notifications.uploadError', 'Dosya yüklenemedi');
      throw new Error(msg);
    }
    // Key her zaman gelmeli. Fallback: URL'den path'i cek.
    let key = uploadResult.key;
    if (!key && uploadResult.url) {
      try {
        const u = new URL(uploadResult.url);
        key = decodeURIComponent(u.pathname.replace(/^\//, ''));
      } catch { /* noop */ }
    }
    return {
      file_url: key || '',
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type || null,
    };
  }, [t]);

  // Yeni bir dokuman olustur: kayit + ilk versiyon + atamalar (opsiyonel).
  const createDocument = useCallback(async ({
    title, description, file, notes, assignments,
    category, process, isActive, tagIds,
  }) => {
    if (!user) return { success: false, error: 'not-authenticated' };
    setIsCreating(true);
    try {
      // 1) document_bank row
      const createRes = await hasuraClient.mutate(CREATE_DOCUMENT_BANK_MUTATION, {
        companyId: user.companyId,
        title,
        description: description || null,
        createdBy: user.id,
        category: category || null,
        process: process || null,
        isActive: isActive === false ? false : true,
      });
      const docId = createRes?.insert_document_bank_one?.id;
      if (!docId) throw new Error('create-failed');

      // 2) upload + first version
      const fileMeta = await uploadFile(file);
      await hasuraClient.mutate(CREATE_DOCUMENT_BANK_VERSION_MUTATION, {
        documentId: docId,
        versionNumber: 1,
        fileUrl: fileMeta.file_url,
        fileName: fileMeta.file_name,
        fileSize: fileMeta.file_size,
        mimeType: fileMeta.mime_type,
        notes: notes || null,
        uploadedBy: user.id,
      });

      // 3) assignments
      if (Array.isArray(assignments) && assignments.length > 0) {
        const objects = assignments.map(a => ({
          document_id: docId,
          assignment_type: a.type,
          assignment_ref_id: a.refId,
          created_by: user.id,
        }));
        await hasuraClient.mutate(REPLACE_DOCUMENT_BANK_ASSIGNMENTS_MUTATION, {
          documentId: docId,
          objects,
        });
      }

      // 4) tag assignments (opsiyonel)
      if (Array.isArray(tagIds) && tagIds.length > 0) {
        const tagObjects = tagIds.map(tid => ({
          document_id: docId,
          tag_id: tid,
          created_by: user.id,
        }));
        await hasuraClient.mutate(REPLACE_DOCUMENT_BANK_TAG_ASSIGNMENTS_MUTATION, {
          documentId: docId,
          objects: tagObjects,
        });
      }

      showNotification(t('documentBank.notifications.created', 'Doküman eklendi'), 'success');
      await fetchDocuments();
      return { success: true, id: docId };
    } catch (err) {
      showNotification(err?.message || t('documentBank.notifications.createError', 'Doküman oluşturulamadı'), 'error');
      return { success: false, error: err?.message };
    } finally {
      setIsCreating(false);
    }
  }, [user, uploadFile, fetchDocuments, showNotification, t]);

  // Sadece metadata (title/description/category/process/is_active) guncelleme
  const updateDocumentMeta = useCallback(async ({
    id, title, description, category, process, isActive,
  }) => {
    setIsUpdating(true);
    try {
      await hasuraClient.mutate(UPDATE_DOCUMENT_BANK_MUTATION, {
        id,
        title,
        description: description || null,
        category: category || null,
        process: process || null,
        isActive: isActive === false ? false : true,
      });
      showNotification(t('documentBank.notifications.updated', 'Doküman güncellendi'), 'success');
      await fetchDocuments();
      return { success: true };
    } catch (err) {
      showNotification(err?.message || t('documentBank.notifications.updateError', 'Doküman güncellenemedi'), 'error');
      return { success: false, error: err?.message };
    } finally {
      setIsUpdating(false);
    }
  }, [fetchDocuments, showNotification, t]);

  // Sadece aktif/pasif toggle — hizli row action icin.
  const toggleActive = useCallback(async ({ id, isActive }) => {
    try {
      await hasuraClient.mutate(TOGGLE_DOCUMENT_BANK_ACTIVE_MUTATION, {
        id,
        isActive: !!isActive,
      });
      showNotification(
        isActive
          ? t('documentBank.notifications.activated', 'Doküman aktifleştirildi')
          : t('documentBank.notifications.deactivated', 'Doküman pasifleştirildi'),
        'success',
      );
      await fetchDocuments();
      return { success: true };
    } catch (err) {
      showNotification(err?.message || t('documentBank.notifications.updateError', 'Doküman güncellenemedi'), 'error');
      return { success: false, error: err?.message };
    }
  }, [fetchDocuments, showNotification, t]);

  // Yeni versiyon yukle (dosya + notlar). version_number = mevcut max + 1.
  const uploadNewVersion = useCallback(async ({ documentId, file, notes }) => {
    if (!user) return { success: false, error: 'not-authenticated' };
    setIsUpdating(true);
    try {
      const latest = await hasuraClient.query(GET_LATEST_VERSION_NUMBER_QUERY, { documentId });
      const nextVersion = (latest?.document_bank_versions?.[0]?.version_number || 0) + 1;

      const fileMeta = await uploadFile(file);
      await hasuraClient.mutate(CREATE_DOCUMENT_BANK_VERSION_MUTATION, {
        documentId,
        versionNumber: nextVersion,
        fileUrl: fileMeta.file_url,
        fileName: fileMeta.file_name,
        fileSize: fileMeta.file_size,
        mimeType: fileMeta.mime_type,
        notes: notes || null,
        uploadedBy: user.id,
      });
      showNotification(t('documentBank.notifications.versionAdded', 'Yeni versiyon eklendi'), 'success');
      await fetchDocuments();
      return { success: true };
    } catch (err) {
      showNotification(err?.message || t('documentBank.notifications.versionError', 'Versiyon eklenemedi'), 'error');
      return { success: false, error: err?.message };
    } finally {
      setIsUpdating(false);
    }
  }, [user, uploadFile, fetchDocuments, showNotification, t]);

  const deleteDocument = useCallback(async (id) => {
    setIsDeleting(true);
    try {
      await hasuraClient.mutate(DELETE_DOCUMENT_BANK_MUTATION, { id });
      showNotification(t('documentBank.notifications.deleted', 'Doküman silindi'), 'success');
      await fetchDocuments();
      return { success: true };
    } catch (err) {
      showNotification(err?.message || t('documentBank.notifications.deleteError', 'Doküman silinemedi'), 'error');
      return { success: false, error: err?.message };
    } finally {
      setIsDeleting(false);
    }
  }, [fetchDocuments, showNotification, t]);

  // Atamalari replace et (delete all + re-insert). Assignments = [{type, refId}].
  const replaceAssignments = useCallback(async ({ documentId, assignments }) => {
    if (!user) return { success: false, error: 'not-authenticated' };
    try {
      const objects = (assignments || []).map(a => ({
        document_id: documentId,
        assignment_type: a.type,
        assignment_ref_id: a.refId,
        created_by: user.id,
      }));
      await hasuraClient.mutate(REPLACE_DOCUMENT_BANK_ASSIGNMENTS_MUTATION, {
        documentId,
        objects,
      });
      showNotification(t('documentBank.notifications.assignmentsUpdated', 'Atamalar güncellendi'), 'success');
      await fetchDocuments();
      return { success: true };
    } catch (err) {
      showNotification(err?.message || t('documentBank.notifications.assignmentsError', 'Atamalar güncellenemedi'), 'error');
      return { success: false, error: err?.message };
    }
  }, [user, fetchDocuments, showNotification, t]);

  // Etiket atamalarini replace et. tagIds = [1, 4, 7] — bos array tum
  // etiketlerin kaldirildigi anlamina gelir.
  const replaceTagAssignments = useCallback(async ({ documentId, tagIds }) => {
    if (!user) return { success: false, error: 'not-authenticated' };
    try {
      const objects = (tagIds || []).map(tid => ({
        document_id: documentId,
        tag_id: tid,
        created_by: user.id,
      }));
      await hasuraClient.mutate(REPLACE_DOCUMENT_BANK_TAG_ASSIGNMENTS_MUTATION, {
        documentId,
        objects,
      });
      await fetchDocuments();
      return { success: true };
    } catch (err) {
      showNotification(err?.message || t('documentBank.notifications.tagsError', 'Etiketler güncellenemedi'), 'error');
      return { success: false, error: err?.message };
    }
  }, [user, fetchDocuments, showNotification, t]);

  // DynamicCrudPage'in bekledigi submit callback. FORM_CONFIGS.documentBank
  // dosya + atama + versiyon icin ayni modal'i paylasiyor; hangi is akisinin
  // isletilecegini formData.__mode belirler (DocumentBankPage row action'lari
  // uzerinden set edilir):
  //   'create'      -> createDocument (row + first version + assignments)
  //   'version'     -> uploadNewVersion (yeni versiyon)
  //   'assignments' -> replaceAssignments (sadece atama listesi)
  //   'meta' (def.) -> updateDocumentMeta + opsiyonel yeni versiyon +
  //                    assignments varsa replaceAssignments
  const handleFormSubmit = useCallback(async (formData, id) => {
    const {
      title, description, file, notes, assignments, __mode,
      category, process, isActive, tagIds,
    } = formData || {};
    const mode = __mode || (id ? 'meta' : 'create');

    if (!id) {
      return createDocument({
        title: (title || '').trim(),
        description: description || null,
        file,
        notes,
        assignments,
        category,
        process,
        isActive,
        tagIds,
      });
    }

    if (mode === 'version') {
      if (!file) return { success: false, error: 'no-file' };
      return uploadNewVersion({ documentId: id, file, notes });
    }

    if (mode === 'assignments') {
      return replaceAssignments({ documentId: id, assignments: assignments || [] });
    }

    // 'meta' default akisi: onceki metadata guncelle, ardindan file/assignments
    // varsa ikinci mutasyonu tetikle. Basarisiz olursa erken cik.
    const metaRes = await updateDocumentMeta({
      id,
      title: (title || '').trim(),
      description,
      category,
      process,
      isActive,
    });
    if (!metaRes?.success) return metaRes;

    if (file) {
      const verRes = await uploadNewVersion({ documentId: id, file, notes });
      if (!verRes?.success) return verRes;
    }

    if (Array.isArray(assignments)) {
      const asnRes = await replaceAssignments({ documentId: id, assignments });
      if (!asnRes?.success) return asnRes;
    }

    if (Array.isArray(tagIds)) {
      const tagRes = await replaceTagAssignments({ documentId: id, tagIds });
      if (!tagRes?.success) return tagRes;
    }

    return { success: true };
  }, [createDocument, updateDocumentMeta, uploadNewVersion, replaceAssignments, replaceTagAssignments]);

  return {
    // DynamicCrudPage-uyumlu return shape (InvoiceManagement pattern)
    documentBank: documents,
    isLoading: loading,
    isCreating,
    isUpdating,
    isDeleting,
    isRootUser,
    handleFormSubmit,
    deleteDocumentBank: deleteDocument,
    // FilterConfig ve upload form'unun etiket dropdown'una besleyecegimiz veri.
    additionalData: { tags },

    // Custom modallar icin ek metotlar (dosya + versiyon + atama)
    fetchDocuments,
    tags,
    createDocument,
    updateDocumentMeta,
    toggleActive,
    uploadNewVersion,
    replaceAssignments,
    replaceTagAssignments,
  };
};

export { useDocumentBank };
